/**
 * Email Verification Page
 *
 * Shown after user registers - prompts user to verify their email address.
 * User receives email with verification link that brings them back here with a token.
 * After verification, user can proceed to login.
 *
 * States:
 * 1. "check-email" - Initial state: check your email message
 * 2. "verifying" - User clicked link, verifying token
 * 3. "verified" - Success state, redirect to login
 * 4. "expired" - Token expired, can resend
 * 5. "error" - Verification failed
 *
 * Usage:
 *   <Route path="/verify-email" element={<EmailVerificationPage />} />
 */

import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { AuthShell, AuthTitle, AuthField, AuthSubmit, AuthNote, AuthGhost, authLink } from '@/components/auth/AuthShell';
import { apiClient } from '@/services/api';

type VerificationState = 'check-email' | 'verifying' | 'verified' | 'expired' | 'error';

export const EmailVerificationPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const email = searchParams.get('email');

  const [state, setState] = useState<VerificationState>('check-email');
  const [error, setError] = useState<string | null>(null);
  const [resendEmail, setResendEmail] = useState(email || '');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  // Auto-verify if token is present in URL.
  // The ref guard prevents the double-invocation in React 18 StrictMode from
  // sending two simultaneous requests (the second would get "token already used").
  const verifyCalledRef = React.useRef(false);
  useEffect(() => {
    if (token && !verifyCalledRef.current) {
      verifyCalledRef.current = true;
      verifyEmailToken(token);
    }
  }, [token]);

  const verifyEmailToken = async (verificationToken: string) => {
    try {
      setState('verifying');
      setError(null);

      // Call backend to verify the email token
      const response = await apiClient.post('/api/v1/auth/verify-email', {
        token: verificationToken,
      });

      // Success - verified
      setState('verified');

      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate('/login', {
          state: {
            message: 'Email verified successfully! Please login to your account.',
            email: response.data.email,
          },
          replace: true,
        });
      }, 2000);
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Verification failed';

      // Check if token is expired
      if (err.response?.status === 410 || errorMessage.includes('expired')) {
        setState('expired');
        setError('Verification link has expired. Please request a new one.');
      } else {
        setState('error');
        setError(errorMessage);
      }
    }
  };

  const handleResendEmail = async () => {
    if (!resendEmail) {
      setError('Please enter your email address');
      return;
    }

    try {
      setResendLoading(true);
      setError(null);
      setResendSuccess(false);

      // Call backend to resend verification email
      await apiClient.post('/api/v1/auth/resend-verification-email', {
        email: resendEmail,
      });

      setResendSuccess(true);
      setResendEmail('');

      // Hide success message after 5 seconds
      setTimeout(() => {
        setResendSuccess(false);
      }, 5000);
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.detail || err.response?.data?.message || 'Failed to resend email';
      setError(errorMessage);
    } finally {
      setResendLoading(false);
    }
  };

  const resend = (
    <div className="flex flex-col gap-3">
      {resendSuccess && <AuthNote tone="ok">A new link is on its way. Check your inbox and spam folder.</AuthNote>}
      {error && state === 'check-email' && <AuthNote>{error}</AuthNote>}
      <AuthField label="Email" type="email" value={resendEmail} onChange={(e) => setResendEmail(e.target.value)} placeholder="you@example.com" autoComplete="email" />
      <AuthGhost onClick={handleResendEmail} disabled={resendLoading}>{resendLoading ? 'Sending…' : 'Send a new link'}</AuthGhost>
    </div>
  );

  return (
    <AuthShell headline={<>One click and you’re in.</>} sub="We sent a link to your email. It works for 24 hours.">
      {state === 'check-email' && (
        <div className="flex flex-col gap-4">
          <span className="text-label uppercase text-[#646D80]">Verify email · sent</span>
          <AuthTitle sub={email ? <>Open the link we sent to <strong className="text-[#0F1526]">{email}</strong>.</> : 'Open the link we sent to your email.'}>Check your email</AuthTitle>
          <p className="m-0 text-sm text-[#4A5468]">Didn’t get it? Send it again:</p>
          {resend}
          <p className="m-0 text-center text-sm text-[#4A5468]"><Link to="/login" className={authLink}>Back to sign in</Link></p>
        </div>
      )}
      {state === 'verifying' && <AuthTitle sub="One moment.">Checking your link…</AuthTitle>}
      {state === 'verified' && (
        <div className="flex flex-col gap-4">
          <span className="text-label uppercase text-[#14603F]">Verified</span>
          <AuthTitle sub="Taking you to sign in…">You’re in.</AuthTitle>
          <AuthSubmit type="button" onClick={() => navigate('/login', { replace: true })}>Sign in</AuthSubmit>
        </div>
      )}
      {(state === 'expired' || state === 'error') && (
        <div className="flex flex-col gap-4">
          <span className="text-label uppercase text-[#B42318]">{state === 'expired' ? 'Link expired' : 'Couldn’t verify'}</span>
          <AuthTitle sub={error || 'This link is older than 24 hours.'}>{state === 'expired' ? 'This link is too old' : 'That link didn’t work'}</AuthTitle>
          {resend}
        </div>
      )}
    </AuthShell>
  );
};

export default EmailVerificationPage;
